import type { ReactNode } from 'react';

import { LogoMark, Wordmark } from '@/components/Logo';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';

interface AuthShellProps {
  title: string;
  description: ReactNode;
  children: ReactNode;
}

/** Sign-in screens, the template's: the logo over one centred card holding the form. */
export function AuthShell({ title, description, children }: AuthShellProps) {
  return (
    <main className="grid min-h-svh place-items-center bg-muted/40 px-4 py-10">
      <div className="grid w-full max-w-sm gap-6">
        <div className="flex items-center justify-center gap-2.5">
          <LogoMark className="size-10" />
          <span className="grid leading-tight">
            <Wordmark className="text-xl" />
            <span className="text-xs text-muted-foreground">Một cổng Zalo cho mọi hệ thống</span>
          </span>
        </div>
        <Card className="gap-5">
          <CardHeader>
            <h1 id="auth-title" className="text-lg leading-none font-semibold tracking-tight">
              {title}
            </h1>
            <CardDescription>{description}</CardDescription>
          </CardHeader>
          <CardContent>
            <section aria-labelledby="auth-title" className="grid gap-4">
              {children}
            </section>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}

/** The quiet text link of the sign-in screens (back, sign out). */
export function AuthLink({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="justify-self-start py-1 text-sm font-medium text-primary underline decoration-transparent underline-offset-[3px] transition-colors hover:decoration-current focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
    >
      {children}
    </button>
  );
}

/** An error under a sign-in form. */
export function AuthError({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="rounded-md border-l-[3px] border-l-destructive bg-danger-soft px-3.5 py-2.5 text-sm text-destructive"
    >
      {children}
    </p>
  );
}
